import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Save, Send, Mail, Plus, Trash2 } from 'lucide-react';

const AdminTabOnboarding = () => {
  const [roles, setRoles] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/onboarding`);
      setRoles(res.data.roles || []);
      if (res.data.roles?.length) {
        setActive(res.data.roles[0].role);
        setForm(JSON.parse(JSON.stringify(res.data.roles[0])));
      }
    } catch (e) { toast.error('Failed to load onboarding content'); }
  };

  useEffect(() => { load(); }, []);

  const selectRole = (r) => {
    const doc = roles.find((x) => x.role === r);
    setActive(r);
    setForm(JSON.parse(JSON.stringify(doc)));
  };

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const updateBullet = (i, val) => {
    const b = [...(form.email_bullets || [])]; b[i] = val; update('email_bullets', b);
  };
  const addBullet = () => update('email_bullets', [...(form.email_bullets || []), '']);
  const removeBullet = (i) => update('email_bullets', form.email_bullets.filter((_, idx) => idx !== i));

  const updateChecklist = (i, key, val) => {
    const c = [...(form.checklist || [])]; c[i] = { ...c[i], [key]: val }; update('checklist', c);
  };
  const addChecklistItem = () => update('checklist', [...(form.checklist || []), { id: `item_${Date.now()}`, label: '', description: '', action_label: 'Go', action_url: '/dashboard' }]);
  const removeChecklistItem = (i) => update('checklist', form.checklist.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        welcome_title: form.welcome_title,
        welcome_message: form.welcome_message,
        email_subject: form.email_subject,
        email_intro: form.email_intro,
        email_bullets: (form.email_bullets || []).filter((b) => b.trim()),
        checklist: form.checklist,
        dashboard_path: form.dashboard_path,
      };
      await axios.put(`${API_URL}/admin/onboarding/${active}`, payload);
      toast.success('Onboarding content saved');
      await load();
      setActive(active);
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/admin/onboarding/${active}/test-email`, { email: testEmail || undefined });
      if (res.data.sent) toast.success(`Test email sent to ${res.data.to}`);
      else toast.warning(res.data.note || 'Email not sent (Resend not configured)');
    } catch (e) { toast.error('Test email failed'); }
    setSending(false);
  };

  if (!form) return <div className="p-8 text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="admin-onboarding-tab">
      <h2 className="font-serif text-xl font-bold text-primary mb-1">Onboarding &amp; Welcome Emails</h2>
      <p className="text-sm text-gray-500 mb-4">Edit the welcome page, checklist, and welcome email each role sees — no code changes needed.</p>

      {/* Role switcher */}
      <div className="flex flex-wrap gap-2 mb-6">
        {roles.map((r) => (
          <button
            key={r.role}
            onClick={() => selectRole(r.role)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border ${active === r.role ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200 hover:border-primary/40'}`}
            data-testid={`onboarding-role-${r.role}`}
          >
            {r.label || r.role}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Welcome page + checklist */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800 border-b pb-2">Welcome Page</h3>
          <div>
            <Label className="text-xs">Welcome Title</Label>
            <Input value={form.welcome_title || ''} onChange={(e) => update('welcome_title', e.target.value)} data-testid="onboarding-welcome-title" />
          </div>
          <div>
            <Label className="text-xs">Welcome Message</Label>
            <Textarea value={form.welcome_message || ''} onChange={(e) => update('welcome_message', e.target.value)} rows={3} />
          </div>
          <div>
            <Label className="text-xs">Dashboard Path (redirect)</Label>
            <Input value={form.dashboard_path || ''} onChange={(e) => update('dashboard_path', e.target.value)} />
          </div>

          <div className="flex items-center justify-between border-b pb-2 mt-4">
            <h3 className="font-semibold text-gray-800">Getting Started Checklist</h3>
            <Button size="sm" variant="outline" onClick={addChecklistItem}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
          </div>
          {(form.checklist || []).map((c, i) => (
            <div key={i} className="border border-gray-100 rounded-lg p-3 space-y-2 bg-gray-50" data-testid={`onboarding-checklist-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-gray-400">{c.id}</span>
                <button onClick={() => removeChecklistItem(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <Input placeholder="Label" value={c.label || ''} onChange={(e) => updateChecklist(i, 'label', e.target.value)} />
              <Input placeholder="Description" value={c.description || ''} onChange={(e) => updateChecklist(i, 'description', e.target.value)} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Button label" value={c.action_label || ''} onChange={(e) => updateChecklist(i, 'action_label', e.target.value)} />
                <Input placeholder="Button URL" value={c.action_url || ''} onChange={(e) => updateChecklist(i, 'action_url', e.target.value)} />
              </div>
            </div>
          ))}
        </div>

        {/* Email */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800 border-b pb-2">Welcome Email</h3>
          <div>
            <Label className="text-xs">Subject (use {'{name}'} for first name)</Label>
            <Input value={form.email_subject || ''} onChange={(e) => update('email_subject', e.target.value)} data-testid="onboarding-email-subject" />
          </div>
          <div>
            <Label className="text-xs">Intro paragraph</Label>
            <Textarea value={form.email_intro || ''} onChange={(e) => update('email_intro', e.target.value)} rows={3} />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Bullet points</Label>
            <Button size="sm" variant="outline" onClick={addBullet}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
          </div>
          {(form.email_bullets || []).map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={b} onChange={(e) => updateBullet(i, e.target.value)} data-testid={`onboarding-bullet-${i}`} />
              <button onClick={() => removeBullet(i)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-4">
            <div className="flex items-center gap-2 mb-2 text-blue-800 font-semibold text-sm"><Mail className="w-4 h-4" /> Send Test Email</div>
            <div className="flex gap-2">
              <Input placeholder="test@example.com (blank = your email)" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="onboarding-test-email" />
              <Button onClick={sendTest} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap" data-testid="onboarding-send-test">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Send Test</>}
              </Button>
            </div>
            <p className="text-xs text-blue-600 mt-2">Sends the current saved version. Save first if you just edited.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6 border-t pt-4">
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-hover text-white" data-testid="onboarding-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminTabOnboarding;
